import React, { useEffect, useState } from 'react';
import { useBunkerContext } from 'src/context/BunkerContext';
import { useVesselStore } from 'src/context/VesselContext';
import { FuelROB } from 'src/types/ViewModel/Bunkering/FuelROB';
import {
    checkBunkeringStatusAfterRobUpdate,
    fuelTankTypes,
    getBunkeringEventsInAolPeriod,
    getBunkeringsInAolPeriod,
    getBunkeringStatusAtDate,
    getContentsInTankAtDate,
    isBunkeringOnboardAtDate,
} from '../BunkeringUtils';
import { useAolStore } from 'src/context/AolContext';
import { AolGroup } from 'src/types/ViewModel/AolItem';
import { addDateHours, isDateSameOrBefore } from 'odfjell-date-time-utils/dateTimeUtils';
import BunkerRobTable from './BunkerRobTable';
import { UUID } from 'crypto';
import useGetBunkeringData from 'src/api/bunkering/useGetBunkeringData';
import { mapBunkerReceiveds, mapFuelRobs } from 'src/factory/Bunkering/BunkeringFactory';
import { FuelRobVM } from 'src/types/ViewModel/Bunkering/FuelRobVM';
import { FuelRobColumn } from 'src/types/ViewModel/Bunkering/FuelRobColumn';
import { EmptyDataMessage, EmptyDataPage, RobPageWrapper } from './BunkerRob.styles';
import { RobDateStatusEnum } from 'src/enums/bunkering/robDateStatusEnum';
import { BunkeringStatusEnum } from 'src/types/ViewModel/Bunkering/BunkeringEvent';
import { BunkeringStatusBM } from 'src/types/BusinessModel/Bunkering/BunkeringStatusBM';
import { useAuthStore } from 'src/context/AuthContext';
import { formatAolGroupDate } from 'src/utils/aol/aolDateUtils';
import {
    calculateRob,
    createFuelRob,
    deafultSortFuelTanks,
    findExistingRob,
    getConsumptionData,
    getLossData,
    getRobDateStatus,
    MAX_BDN_COUNT,
} from './RobUtils';
import { BunkerReceived } from 'src/types/ViewModel/Bunkering/BunkerReceived';
import { useAppStore } from 'src/context/AppContext';
import { AppFeatureEnum } from 'src/enums/appFeatureEnum';
import BunkerRobWarningBanner, { BunkerRefWarningLevel } from './BunkerRobWarningBanner';
import { getBunkeringsOnboardAtDate } from '../BunkeringUtils';

const BunkerRobContainer: React.FC = () => {
    const {
        selectedBunkeringDate,
        bunkerings,
        fuelRobData,
        saveBunkeringPageData: saveFuelRobData,
        setSaveBunkeringPageData: setSaveFuelRobData,
        bunkeringEvents,
        bunkerLossData,
        setSaveButtonEnabled,
    } = useBunkerContext();
    const { vessel } = useVesselStore();
    const { aolGroups, aolDataItems } = useAolStore();
    const { loggedInUser } = useAuthStore();
    const { appFeatureSettings } = useAppStore();

    const { upsertFuelRobData, upsertBunkeringStatus, upsertDebunkeringData } = useGetBunkeringData();

    const [fuelRobVM, setFuelRobVM] = useState<FuelRobVM>(null);
    const [aolGroupForDate, setAolGroupForDate] = useState<AolGroup | null>(null);
    const [bunkerRefWarningLevel, setBunkerRefWarningLevel] = useState<BunkerRefWarningLevel>('none');
    const [bunkerRefCount, setBunkerRefCount] = useState<number>(0);

    const updateBunkeringStatusAfterRobUpdate = (fuelRobsToSave: FuelROB[]) => {
        const robDateStatus = getRobDateStatus(
            vessel,
            aolGroupForDate,
            aolDataItems,
            addDateHours(selectedBunkeringDate, 12),
            bunkerings,
            fuelRobsToSave
        );
        if (robDateStatus === RobDateStatusEnum.complete) {
            bunkerings.forEach((bunkering) => {
                const checkedStatus = checkBunkeringStatusAfterRobUpdate(bunkering, fuelRobsToSave);
                const statusAtDate = getBunkeringStatusAtDate(bunkering, addDateHours(selectedBunkeringDate, 12));
                if (
                    isBunkeringOnboardAtDate(bunkering, addDateHours(selectedBunkeringDate, 12)) &&
                    statusAtDate &&
                    checkedStatus !== statusAtDate.status &&
                    (bunkering.currentStatus.status === BunkeringStatusEnum.Approved || bunkering.currentStatus.status === BunkeringStatusEnum.InUse)
                ) {
                    const activeFromDate = new Date(selectedBunkeringDate);
                    if (checkedStatus === BunkeringStatusEnum.Spent) activeFromDate.setDate(activeFromDate.getDate() + 1);

                    const newStatus = {
                        bunkeringStatusId: 0,
                        bunkeringStatusGuid: window.crypto.randomUUID(),
                        bunkeringId: bunkering.bunkeringId,
                        bunkeringGuid: bunkering.bunkeringGuid,
                        status: checkedStatus,
                        activeFromDate: activeFromDate,
                        updatedBy: loggedInUser.userName ?? loggedInUser.appAccessToken,
                        updatedAt: new Date(),
                    } as BunkeringStatusBM;
                    upsertBunkeringStatus(newStatus, vessel.imoNumber);
                }
            });
        }
    };

    useEffect(() => {
        if (saveFuelRobData && fuelRobVM) {
            const fuelRobsToSave = fuelRobVM.fuelRobColumns
                .filter((col) => !col.debunkeringEventId)
                .flatMap((col) => col.fuelRobs.filter((rob) => !rob.displayOnly));
            fuelRobVM.fuelRobColumns
                .filter((col) => col.debunkeringEventId)
                .forEach((col) => {
                    const debunkeringData = col.debunkeringPerTank.filter((debunker) => debunker.bunkerReceivedMt > 0);
                    upsertDebunkeringData(mapBunkerReceiveds(debunkeringData), vessel.imoNumber);
                });
            upsertFuelRobData(mapFuelRobs(fuelRobsToSave), vessel.imoNumber);
            setSaveFuelRobData(false);
            updateBunkeringStatusAfterRobUpdate(fuelRobsToSave);
        }
    }, [saveFuelRobData]);

    const addRobColumnsToVM = (_fuelRobVM: FuelRobVM, _aolGroup: AolGroup) => {
        _aolGroup.aolDataItems.forEach((aolItem) => {
            const bunkeringsInAolPeriod = getBunkeringsInAolPeriod(aolItem.reportFromTimeLocalTime, aolItem.reportToTimeLocalTime, bunkerings).sort(
                (a, b) => (isDateSameOrBefore(a.bunkeringDateTime, b.bunkeringDateTime) ? -1 : 1)
            );
            if (bunkeringsInAolPeriod.length > 0)
                bunkeringsInAolPeriod.forEach((b) =>
                    _fuelRobVM.fuelRobColumns.push({ bunkeringGuid: b.bunkeringGuid, fuelRobs: [] } as FuelRobColumn)
                );
            const debunkeringsInAolPeriod = getBunkeringEventsInAolPeriod(
                aolItem.reportFromTimeLocalTime,
                aolItem.reportToTimeLocalTime,
                bunkeringEvents.filter((e) => e.debunkering)
            );
            debunkeringsInAolPeriod.forEach((db) => {
                const bunkering = bunkerings.find((b) => b.debunkeringEventIds.includes(db.eventID as UUID));
                if (bunkering)
                    _fuelRobVM.fuelRobColumns.push({
                        bunkeringGuid: bunkering.bunkeringGuid,
                        debunkeringEventId: db.eventID,
                        fuelRobs: [],
                        debunkeringPerTank: [],
                    } as FuelRobColumn);
            });
            if (aolItem.noonReport?.metadata?.noonReportId)
                _fuelRobVM.fuelRobColumns.push({ noonReportId: aolItem.noonReport?.metadata?.noonReportId ?? 0, fuelRobs: [] } as FuelRobColumn);
        });
    };

    const addDebunkeringEntry = (col, tank, bunkering) => {
        const existingDebunkerPerTank = bunkering.debunkeringData.find(
            (b) => b.tankId === tank.tankId && b.debunkeringEventId === col.debunkeringEventId
        );
        if (!existingDebunkerPerTank) {
            col.debunkeringPerTank.push({
                bunkerInTankId: 0,
                tankId: tank.tankId,
                bunkeringGuid: bunkering.bunkeringGuid,
                debunkeringEventId: col.debunkeringEventId,
                bunkerReceivedMt: 0,
            } as BunkerReceived);
        } else {
            col.debunkeringPerTank.push(existingDebunkerPerTank);
        }
    };

    const addFuelRobsToColumns = (_fuelRobVM: FuelRobVM, _aolGroup: AolGroup) => {
        let previousColumn: FuelRobColumn = _fuelRobVM.previousRobColumn;
        _fuelRobVM.fuelRobColumns.forEach((col) => {
            vessel.nonCargoTanks
                .filter((tank) => fuelTankTypes.includes(tank.tankType))
                .forEach((tank) => {
                    const contentInTank = getContentsInTankAtDate(
                        bunkerings,
                        tank.tankId,
                        addDateHours(selectedBunkeringDate, 12),
                        _aolGroup,
                        aolDataItems,
                        fuelRobData,
                        appFeatureSettings
                            ?.find((f) => f.appFeatureId === AppFeatureEnum.Bunkering)
                            ?.vessels?.find((v) => v.imoNumber === vessel.imoNumber.toString())
                    );
                    contentInTank.forEach((bunkering) => {
                        if (
                            col.debunkeringEventId &&
                            col.bunkeringGuid === bunkering.bunkeringGuid &&
                            bunkering.debunkeringEventIds.includes(col.debunkeringEventId)
                        ) {
                            addDebunkeringEntry(col, tank, bunkering);
                        }
                        const existingRob = !col.debunkeringEventId && findExistingRob(fuelRobData, tank, bunkering, col);
                        const consumed = getConsumptionData(_aolGroup, bunkering, tank, col);
                        const loss = getLossData(bunkerLossData, tank, col, bunkering);
                        const calculatedRob = calculateRob(previousColumn, tank, bunkering, col, consumed, loss);

                        if (existingRob) {
                            existingRob.consumed = consumed;
                            existingRob.loss = loss;
                            existingRob.calculatedRob = calculatedRob;
                            if (existingRob.fuelRobId === 0) {
                                existingRob.rob = calculatedRob >= 0 ? calculatedRob : 0;
                            }
                            col.fuelRobs.push(existingRob);
                            return;
                        }

                        const _fuelRob = createFuelRob(tank, bunkering, col, calculatedRob, consumed, loss);
                        col.fuelRobs.push(_fuelRob);
                    });
                });
            previousColumn = col;
        });
    };

    useEffect(() => {
        if (!aolGroups || !selectedBunkeringDate || !fuelRobData || aolGroups.length < 1 || !bunkerings || bunkerings.length < 1) return;

        const _fuelRobVM = {
            date: selectedBunkeringDate,
            fuelRobColumns: [],
        } as FuelRobVM;

        const _aolGroup = aolGroups.find((group) => group.aolDate === formatAolGroupDate(selectedBunkeringDate));
        if (!_aolGroup) return;
        setAolGroupForDate(_aolGroup);

        addRobColumnsToVM(_fuelRobVM, _aolGroup);

        const aolGroupIndex = aolGroups.findIndex((group) => group.aolDate === _aolGroup.aolDate);
        const previousAolItem =
            aolGroupIndex > 0 ? aolGroups[aolGroupIndex - 1].aolDataItems[aolGroups[aolGroupIndex - 1].aolDataItems.length - 1] : null;
        if (previousAolItem?.noonReport?.metadata?.noonReportId) {
            _fuelRobVM.previousRobColumn = {
                noonReportId: previousAolItem.noonReport?.metadata?.noonReportId,
                fuelRobs: fuelRobData.filter((rob) => rob.noonReportId && rob.noonReportId === previousAolItem?.noonReport?.metadata?.noonReportId),
            } as FuelRobColumn;
        }

        addFuelRobsToColumns(_fuelRobVM, _aolGroup);

        setSaveButtonEnabled(
            _fuelRobVM.fuelRobColumns.some(
                (col) =>
                    (!col.debunkeringEventId && col.fuelRobs.some((rob) => !rob.displayOnly)) ||
                    (col.debunkeringEventId && col.debunkeringPerTank.some((debunker) => debunker.bunkerReceivedMt > 0))
            )
        );

        // Check bunker ref count and determine warning level
        const onboardBunkerings = getBunkeringsOnboardAtDate(bunkerings, addDateHours(selectedBunkeringDate, 12));
        const count = onboardBunkerings.length;
        setBunkerRefCount(count);

        if (count > MAX_BDN_COUNT) {
            setBunkerRefWarningLevel('exceeded');
        } else if (count === MAX_BDN_COUNT) {
            setBunkerRefWarningLevel('atLimit');
        } else {
            setBunkerRefWarningLevel('none');
        }

        setFuelRobVM(_fuelRobVM);
    }, [aolGroups, selectedBunkeringDate, fuelRobData, bunkerings, bunkerLossData]);

    const handleFuelRobChange = (bunkeringGuid: UUID, tankId: number, column: FuelRobColumn, robValue: number) => {
        const _vm = { ...fuelRobVM };
        const _column = _vm.fuelRobColumns.find((col) =>
            column.noonReportId ? col.noonReportId === column.noonReportId : col.bunkeringGuid === column.bunkeringGuid
        );
        if (!_column) return;

        const _fuelRob = _column.fuelRobs.find((rob) => rob.tankId === tankId && rob.bunkeringGuid === bunkeringGuid);
        if (!_fuelRob) return;

        _fuelRob.rob = robValue;

        setFuelRobVM(_vm);
    };

    const handleToggleSoundingVerified = (bunkeringGuid: UUID, tankId: number, column: FuelRobColumn, soundingVerified: boolean) => {
        const _vm = { ...fuelRobVM };
        const _column = _vm.fuelRobColumns.find((col) =>
            column.noonReportId ? col.noonReportId === column.noonReportId : col.bunkeringGuid === column.bunkeringGuid
        );
        if (!_column) return;

        const _fuelRob = _column.fuelRobs.find((rob) => rob.tankId === tankId && rob.bunkeringGuid === bunkeringGuid);
        if (!_fuelRob) return;

        _fuelRob.soundingVerified = soundingVerified;

        setFuelRobVM(_vm);
    };

    const handleDebunkeringPerTankChange = (
        tankId: number,
        column: FuelRobColumn,
        previousColumn: FuelRobColumn,
        bunkerReceivedMt: number,
        oldValue: number
    ) => {
        const _vm = { ...fuelRobVM };
        const colIndex = _vm.fuelRobColumns.findIndex((col) => col.debunkeringEventId && col.debunkeringEventId === column.debunkeringEventId);
        const _column = _vm.fuelRobColumns?.[colIndex];
        if (!_column) return;

        const nextColumn = _vm.fuelRobColumns?.[colIndex + 1];

        if (nextColumn) {
            const nextRob = nextColumn.fuelRobs.find((rob) => rob.tankId === tankId && rob.bunkeringGuid === column.bunkeringGuid);
            if (nextRob) {
                nextRob.calculatedRob = Number((nextRob.calculatedRob + (oldValue ?? 0) - bunkerReceivedMt).toFixed(2));
                nextRob.rob = Number((nextRob.rob + (oldValue ?? 0) - bunkerReceivedMt).toFixed(2));
            }
        }

        const _debunkeringPerTank = _column.debunkeringPerTank.find(
            (debunker) =>
                debunker.tankId === tankId &&
                debunker.bunkeringGuid === column.bunkeringGuid &&
                debunker.debunkeringEventId === column.debunkeringEventId
        );
        if (!_debunkeringPerTank) return;

        const _rob = _column.fuelRobs.find((rob) => rob.tankId === tankId && rob.bunkeringGuid === column.bunkeringGuid);
        const previousRob = previousColumn?.fuelRobs.find(
            (_prevRob) => _rob.tankId === _prevRob.tankId && _rob.bunkeringGuid === _prevRob.bunkeringGuid
        );
        if (_rob) {
            _rob.calculatedRob = Number(((previousRob?.rob ?? 0) - bunkerReceivedMt).toFixed(2));
        }

        _debunkeringPerTank.bunkerReceivedMt = bunkerReceivedMt;

        setFuelRobVM(_vm);
    };

    const handleTransferFuel = (column: FuelRobColumn, bunkeringGuid: UUID, fromTankId: number, toTankId: number, transferValue: number) => {
        const _vm = { ...fuelRobVM };
        const _column = _vm.fuelRobColumns.find((col) => col.noonReportId === column.noonReportId);
        if (!_column) return;

        const fromRob = _column.fuelRobs.find((rob) => rob.tankId === fromTankId && rob.bunkeringGuid === bunkeringGuid);
        let toRob = _column.fuelRobs.find((rob) => rob.tankId === toTankId && rob.bunkeringGuid === bunkeringGuid);

        if (!fromRob) return;

        if (!toRob) {
            toRob = {
                fuelRobId: 0,
                tankId: toTankId,
                bunkeringGuid: bunkeringGuid,
                noonReportId: column.noonReportId,
                rob: 0,
                consumed: 0,
            } as FuelROB;
            _vm.fuelRobColumns.forEach((col) => {
                if (col.noonReportId === column.noonReportId) col.fuelRobs.push(toRob);
                else {
                    col.fuelRobs.push({ ...toRob, displayOnly: true } as FuelROB);
                }
            });
        }

        fromRob.rob = Number((fromRob.rob - transferValue).toFixed(2));
        toRob.rob = Number((toRob.rob + transferValue).toFixed(2));

        setFuelRobVM(_vm);
    };

    return fuelRobVM?.fuelRobColumns?.length > 0 ? (
        <RobPageWrapper>
            {bunkerRefWarningLevel !== 'none' && (
                <BunkerRobWarningBanner bunkerRefCount={bunkerRefCount} maxBdnCount={MAX_BDN_COUNT} warningLevel={bunkerRefWarningLevel} />
            )}
            <BunkerRobTable
                fuelRobVM={fuelRobVM}
                aolGroup={aolGroupForDate}
                selectedBunkeringDate={selectedBunkeringDate}
                fuelTanks={deafultSortFuelTanks(vessel)}
                changeFuelRob={handleFuelRobChange}
                changeSoundingVerified={handleToggleSoundingVerified}
                changeDebunkering={handleDebunkeringPerTankChange}
                transferFuel={handleTransferFuel}
            />
        </RobPageWrapper>
    ) : (
        <EmptyDataPage>
            <EmptyDataMessage>No data. Please create AoLs and log consumption first.</EmptyDataMessage>
        </EmptyDataPage>
    );
};

export default BunkerRobContainer;
